/**
 * Utility functions for handling contact filters
 */

/**
 * Helper function to handle common date filters (createdAt, optinAt, optoutAt)
 */
export const handleDateFilter = (fieldName, operator, value) => {
  if (operator === "overAllTime") {
    // Treat "over all time" as "more than -1 day ago" (before tomorrow)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return {
      [fieldName]: {
        lessThan: tomorrow.toISOString(),
      },
    };
  }

  if (!value || (typeof value === "string" && value.trim() === "")) {
    return null;
  }

  if (operator === "moreThan") {
    // "more than X days ago" - contact was created/opted before (earlier than) the specified date
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() - parseInt(value) + 1);
    targetDate.setHours(0, 0, 0, 0); // Normalize to midnight
    return { [fieldName]: { lessThan: targetDate.toISOString() } };
  } else if (operator === "lessThan") {
    // "less than X days ago" - contact was created/opted after (more recent than) the specified date
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() - parseInt(value));
    targetDate.setHours(0, 0, 0, 0); // Normalize to midnight
    return { [fieldName]: { greaterThan: targetDate.toISOString() } };
  } else if (operator === "after") {
    // "after [date]" - contact was created/opted after the specified date
    const targetDate = new Date(value);
    if (isNaN(targetDate.getTime())) return null; // Invalid date
    targetDate.setHours(23, 59, 59, 999); // End of the day
    return { [fieldName]: { greaterThan: targetDate.toISOString() } };
  } else if (operator === "before") {
    // "before [date]" - contact was created/opted before the specified date
    const targetDate = new Date(value);
    if (isNaN(targetDate.getTime())) return null; // Invalid date
    targetDate.setHours(0, 0, 0, 0); // Start of the day
    return { [fieldName]: { lessThan: targetDate.toISOString() } };
  } else if (operator === "on") {
    // "on [date]" - contact was created/opted on the exact date
    const startDate = new Date(value);
    if (isNaN(startDate.getTime())) return null; // Invalid date
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(value);
    endDate.setHours(23, 59, 59, 999);
    return {
      AND: [
        { [fieldName]: { greaterThan: startDate.toISOString() } },
        { [fieldName]: { lessThan: endDate.toISOString() } },
      ],
    };
  }
  return null;
};

/**
 * Handle birthday filter logic (similar to date filters but with slight differences)
 */
export const handleBirthdayFilter = (operator, value) => {
  if (operator === "overAllTime") {
    // Treat "over all time" as "more than -1 day ago" (before tomorrow)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return {
      birthday: {
        lessThan: tomorrow.toISOString(),
      },
    };
  }

  if (!value || (typeof value === "string" && value.trim() === "")) {
    return null;
  }

  if (operator === "moreThan") {
    // "more than X days ago" - contact birthday is before (older than) the specified date
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() - parseInt(value));
    return { birthday: { lessThan: targetDate.toISOString() } };
  } else if (operator === "lessThan") {
    // "less than X days ago" - contact birthday is after (more recent than) the specified date
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() - parseInt(value));
    return { birthday: { greaterThan: targetDate.toISOString() } };
  } else if (operator === "after") {
    // "after [date]" - contact birthday is after the specified date
    const targetDate = new Date(value);
    if (isNaN(targetDate.getTime())) return null; // Invalid date
    targetDate.setHours(23, 59, 59, 999); // End of the day
    return { birthday: { greaterThan: targetDate.toISOString() } };
  } else if (operator === "before") {
    // "before [date]" - contact birthday is before the specified date
    const targetDate = new Date(value);
    if (isNaN(targetDate.getTime())) return null; // Invalid date
    targetDate.setHours(0, 0, 0, 0); // Start of the day
    return { birthday: { lessThan: targetDate.toISOString() } };
  } else if (operator === "on") {
    // "on [date]" - contact birthday is on the exact date
    const startDate = new Date(value);
    if (isNaN(startDate.getTime())) return null; // Invalid date
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(value);
    endDate.setHours(23, 59, 59, 999);
    endDate.setDate(endDate.getDate() + 1);
    return {
      AND: [
        { birthday: { greaterThan: startDate.toISOString() } },
        { birthday: { lessThan: endDate.toISOString() } },
      ],
    };
  }
  return null;
};

/**
 * Handle received message filter logic
 */
export const handleReceivedMessageFilter = (
  operator,
  value,
  frequency = "atLeastOnce",
  campaignType = "any",
  campaignId = null,
) => {
  // Use the same date filter logic as createdAt, but for messages
  const dateFilter = handleDateFilter("createdAt", operator, value);
  if (!dateFilter) return null;

  // Handle different date filter structures
  let messageDateConditions = [];

  if (dateFilter.createdAt) {
    // For simple operators (moreThan, lessThan, after, before)
    messageDateConditions.push({ createdAt: dateFilter.createdAt });
  } else if (dateFilter.AND) {
    // For "on" operator which returns { AND: [...] }
    messageDateConditions = dateFilter.AND.map((condition) => {
      // Convert createdAt conditions to messages.createdAt
      if (condition.createdAt) {
        return { createdAt: condition.createdAt };
      }
      return condition;
    });
  }
  // For "overAllTime" operator, dateFilter is {} and messageDateConditions remains empty

  // Build the base message criteria
  const baseMessageCriteria = {
    AND: [
      { campaignId: { isSet: true } },
      { direction: { equals: "outbound" } },
      {
        OR: [
          { sentAt: { isSet: true } },
          { deliveredAt: { isSet: true } },
          { readAt: { isSet: true } },
        ],
      },
      ...messageDateConditions, // Spread the date conditions
    ],
  };

  // Add campaign filtering if specified
  if (campaignType !== "any" && campaignId) {
    baseMessageCriteria.AND.push({
      campaignId: { equals: campaignId },
    });
  }

  // Apply frequency logic
  if (frequency === "atLeastOnce") {
    return {
      messages: {
        some: baseMessageCriteria,
      },
    };
  } else if (frequency === "zeroTimes") {
    return {
      messages: {
        every: {
          NOT: baseMessageCriteria,
        },
      },
    };
  }

  return null;
};

/**
 * Handle placed order filter logic
 */
export const handlePlacedOrderFilter = (
  operator,
  value,
  frequency = "atLeastOnce",
  campaignType = "any",
  campaignId = null,
) => {
  // Use the same date filter logic as createdAt, but for messages with attributedShopifyOrder
  const dateFilter = handleDateFilter("createdAt", operator, value);
  if (!dateFilter) return null;

  // Handle different date filter structures
  let messageDateConditions = [];

  if (dateFilter.createdAt) {
    // For simple operators (moreThan, lessThan, after, before)
    messageDateConditions.push({ createdAt: dateFilter.createdAt });
  } else if (dateFilter.AND) {
    // For "on" operator which returns { AND: [...] }
    messageDateConditions = dateFilter.AND.map((condition) => {
      // Convert createdAt conditions to messages.createdAt
      if (condition.createdAt) {
        return { createdAt: condition.createdAt };
      }
      return condition;
    });
  }
  // For "overAllTime" operator, dateFilter is {} and messageDateConditions remains empty

  // Build the base message criteria
  const baseMessageCriteria = {
    AND: [
      { attributedShopifyOrder: { isSet: true } },
      { direction: { equals: "outbound" } },
      {
        OR: [
          { sentAt: { isSet: true } },
          { deliveredAt: { isSet: true } },
          { readAt: { isSet: true } },
        ],
      },
      ...messageDateConditions, // Spread the date conditions
    ],
  };

  // Add date filtering on attributedShopifyOrder.processedAt if we have date conditions
  if (messageDateConditions.length > 0) {
    // Replace createdAt conditions with attributedShopifyOrder.processedAt conditions
    const orderDateConditions = messageDateConditions.map((condition) => {
      if (condition.createdAt) {
        return {
          attributedShopifyOrder: {
            processedAt: condition.createdAt,
          },
        };
      }
      return condition;
    });

    // Remove the original createdAt conditions and add the order date conditions
    baseMessageCriteria.AND = baseMessageCriteria.AND.filter(
      (condition) => !condition.createdAt,
    );
    baseMessageCriteria.AND.push(...orderDateConditions);
  }

  // Add campaign filtering if specified
  if (campaignType !== "any" && campaignId) {
    baseMessageCriteria.AND.push({
      campaignId: { equals: campaignId },
    });
  }

  // Apply frequency logic
  if (frequency === "atLeastOnce") {
    return {
      messages: {
        some: baseMessageCriteria,
      },
    };
  } else if (frequency === "zeroTimes") {
    return {
      messages: {
        every: {
          NOT: baseMessageCriteria,
        },
      },
    };
  }

  return null;
};

/**
 * Handle opened message filter logic
 */
export const handleOpenedMessageFilter = (
  operator,
  value,
  frequency = "atLeastOnce",
  campaignType = "any",
  campaignId = null,
) => {
  // Use the same date filter logic as readAt, but for messages
  const dateFilter = handleDateFilter("readAt", operator, value);
  if (!dateFilter) return null;

  // Handle different date filter structures
  let messageDateConditions = [];

  if (dateFilter.readAt) {
    // For simple operators (moreThan, lessThan, after, before)
    messageDateConditions.push({ readAt: dateFilter.readAt });
  } else if (dateFilter.AND) {
    // For "on" operator which returns { AND: [...] }
    messageDateConditions = dateFilter.AND.map((condition) => {
      // Convert readAt conditions to messages.readAt
      if (condition.readAt) {
        return { readAt: condition.readAt };
      }
      return condition;
    });
  }
  // For "overAllTime" operator, dateFilter is {} and messageDateConditions remains empty

  // Build the base message criteria
  const baseMessageCriteria = {
    AND: [
      { campaignId: { isSet: true } },
      { direction: { equals: "outbound" } },
      { readAt: { isSet: true } }, // Only check for readAt being filled
      ...messageDateConditions, // Spread the date conditions
    ],
  };

  // Add campaign filtering if specified
  if (campaignType !== "any" && campaignId) {
    baseMessageCriteria.AND.push({
      campaignId: { equals: campaignId },
    });
  }

  // Apply frequency logic
  if (frequency === "atLeastOnce") {
    return {
      messages: {
        some: baseMessageCriteria,
      },
    };
  } else if (frequency === "zeroTimes") {
    return {
      messages: {
        every: {
          NOT: baseMessageCriteria,
        },
      },
    };
  }

  return null;
};

/**
 * Handle optin status filter logic
 */
export const handleOptinStatusFilter = (operator, value) => {
  if (operator === "is") {
    if (value === "Optin") {
      return { hasOptIn: { equals: true } };
    } else if (value === "Optout") {
      return {
        AND: [{ hasOptIn: { equals: false } }, { optoutAt: { isSet: true } }],
      };
    } else if (value === "Never optin") {
      return {
        AND: [{ hasOptIn: { equals: false } }, { optoutAt: { isSet: false } }],
      };
    }
  } else if (operator === "isNot") {
    if (value === "Optin") {
      return { hasOptIn: { equals: false } };
    } else if (value === "Optout") {
      return {
        OR: [{ hasOptIn: { equals: true } }, { optoutAt: { isSet: false } }],
      };
    } else if (value === "Never optin") {
      return {
        OR: [{ hasOptIn: { equals: true } }, { optoutAt: { isSet: true } }],
      };
    }
  }
  return null;
};

/**
 * Handle custom field filter logic
 */
export const handleCustomFieldFilter = (operator, customFieldName, value) => {
  // Skip if no value is provided (but allow false as a valid value)
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }

  if (operator === "is") {
    // Custom field filtering using matches operator for JSON fields
    return {
      customProperties: {
        matches: { [customFieldName]: value },
      },
    };
  } else if (operator === "isNot") {
    // Custom field "is not" - exclude contacts where the field matches the value
    // This includes contacts where:
    // 1. customProperties field is not set at all
    // 2. customProperties doesn't contain the specific field-value pair
    return {
      OR: [
        // customProperties field is not set
        { customProperties: { isSet: false } },
        // customProperties doesn't contain the specific field-value pair
        {
          NOT: {
            customProperties: {
              matches: { [customFieldName]: value },
            },
          },
        },
      ],
    };
  }
  return null;
};

/**
 * Handle clicked URL filter logic
 */
export const handleClickedUrlFilter = (
  operator,
  value,
  frequency = "atLeastOnce",
  campaignType = "any",
  campaignId = null,
) => {
  // Use the same date filter logic as createdAt, but for urlClickEvents
  const dateFilter = handleDateFilter("createdAt", operator, value);
  if (!dateFilter) return null;

  // Handle different date filter structures
  let urlClickDateConditions = [];

  if (dateFilter.createdAt) {
    // For simple operators (moreThan, lessThan, after, before)
    urlClickDateConditions.push({ createdAt: dateFilter.createdAt });
  } else if (dateFilter.AND) {
    // For "on" operator which returns { AND: [...] }
    urlClickDateConditions = dateFilter.AND.map((condition) => {
      // Convert createdAt conditions to urlClickEvents.createdAt
      if (condition.createdAt) {
        return { createdAt: condition.createdAt };
      }
      return condition;
    });
  }
  // For "overAllTime" operator, dateFilter is {} and urlClickDateConditions remains empty

  // Build the base urlClickEvent criteria
  const baseUrlClickCriteria = {
    AND: [
      { scenario: { isSet: true } },
      ...urlClickDateConditions, // Spread the date conditions
    ],
  };

  // Add campaign filtering if specified
  if (campaignType !== "any" && campaignId) {
    if (campaignType === "chatbot") {
      // For chatbot, filter directly on scenario.id
      baseUrlClickCriteria.AND.push({
        scenario: { id: { equals: campaignId } },
      });
    } else {
      // For campaign/automation, filter on scenario.campaign.id
      baseUrlClickCriteria.AND.push({
        scenario: { campaign: { id: { equals: campaignId } } },
      });
    }
  }

  // Apply frequency logic
  if (frequency === "atLeastOnce") {
    return {
      urlClickEvents: {
        some: baseUrlClickCriteria,
      },
    };
  } else if (frequency === "zeroTimes") {
    return {
      urlClickEvents: {
        every: {
          NOT: baseUrlClickCriteria,
        },
      },
    };
  }

  return null;
};

/**
 * Handle clicked button filter logic (similar to received message but for button clicks)
 */
export const handleClickedButtonFilter = (
  operator,
  value,
  frequency = "atLeastOnce",
  campaignType = "any",
  campaignId = null,
) => {
  // Use the same date filter logic as createdAt, but for messages with isQuickReply
  const dateFilter = handleDateFilter("createdAt", operator, value);
  if (!dateFilter) return null;

  // Handle different date filter structures
  let messageDateConditions = [];

  if (dateFilter.createdAt) {
    // For simple operators (moreThan, lessThan, after, before)
    messageDateConditions.push({ createdAt: dateFilter.createdAt });
  } else if (dateFilter.AND) {
    // For "on" operator which returns { AND: [...] }
    messageDateConditions = dateFilter.AND.map((condition) => {
      // Convert createdAt conditions to messages.createdAt
      if (condition.createdAt) {
        return { createdAt: condition.createdAt };
      }
      return condition;
    });
  }
  // For "overAllTime" operator, dateFilter is {} and messageDateConditions remains empty

  // Build the base message criteria for button clicks
  const baseMessageCriteria = {
    AND: [
      { direction: { equals: "inbound" } },
      { isQuickReply: { equals: true } }, // Filter for quick reply messages
      { replyButtonId: { isSet: true } }, // Filter for messages with reply buttons
      ...messageDateConditions, // Spread the date conditions
    ],
  };

  // Add campaign filtering if specified
  if (campaignType !== "any" && campaignId) {
    if (campaignType === "chatbot") {
      // For chatbot, we need to filter on scenarioId instead of campaignId
      // Note: This assumes messages have a scenarioId field for chatbot messages
      baseMessageCriteria.AND.push({
        scenarioId: { equals: campaignId },
      });
    } else {
      // For campaign/automation, filter via scenario.campaign.id (not campaignId directly)
      baseMessageCriteria.AND.push({
        scenario: { campaign: { id: { equals: campaignId } } },
      });
    }
  }

  // Apply frequency logic
  if (frequency === "atLeastOnce") {
    return {
      messages: {
        some: baseMessageCriteria,
      },
    };
  } else if (frequency === "zeroTimes") {
    return {
      messages: {
        every: {
          NOT: baseMessageCriteria,
        },
      },
    };
  }

  return null;
};

/**
 * Transform a single filter condition into API filter format
 */
export const transformFilterCondition = (condition) => {
  const {
    type,
    operator,
    value,
    tagId,
    customFieldName,
    frequency,
    campaignType,
    campaignId,
  } = condition;

  switch (type) {
    case "phone":
      if (operator === "isSet") {
        return { userPhoneNumber: { isSet: true } };
      } else if (operator === "equals") {
        return { userPhoneNumber: { equals: value } };
      } else if (operator === "startsWith") {
        return { userPhoneNumber: { startsWith: value } };
      }
      break;

    case "tag":
      if (operator === "contains") {
        return {
          tags: { some: { id: { equals: tagId } } },
        };
      } else if (operator === "excludes") {
        return {
          tags: { every: { id: { notEquals: tagId } } },
        };
      }
      break;

    case "optinStatus":
      return handleOptinStatusFilter(operator, value);

    case "firstName":
      if (operator === "is") {
        return { firstName: { equals: value } };
      } else if (operator === "isNot") {
        return { firstName: { notEquals: value } };
      }
      break;

    case "whatsappName":
      if (operator === "is") {
        return { whatsappName: { equals: value } };
      } else if (operator === "isNot") {
        return { whatsappName: { notEquals: value } };
      }
      break;

    case "lastName":
      if (operator === "is") {
        return { lastName: { equals: value } };
      } else if (operator === "isNot") {
        return { lastName: { notEquals: value } };
      }
      break;

    case "createdAt":
      return handleDateFilter("createdAt", operator, value);

    case "optinAt":
      return handleDateFilter("optinAt", operator, value);

    case "optoutAt":
      return handleDateFilter("optoutAt", operator, value);

    case "birthday":
      return handleBirthdayFilter(operator, value);

    case "receivedMessageAt":
      return handleReceivedMessageFilter(
        operator,
        value,
        frequency,
        campaignType,
        campaignId,
      );

    case "placedOrderAt":
      return handlePlacedOrderFilter(
        operator,
        value,
        frequency,
        campaignType,
        campaignId,
      );

    case "openedMessageAt":
      return handleOpenedMessageFilter(
        operator,
        value,
        frequency,
        campaignType,
        campaignId,
      );

    case "clickedUrlAt":
      return handleClickedUrlFilter(
        operator,
        value,
        frequency,
        campaignType,
        campaignId,
      );

    case "clickedButtonAt":
      return handleClickedButtonFilter(
        operator,
        value,
        frequency,
        campaignType,
        campaignId,
      );

    case "customField":
      return handleCustomFieldFilter(operator, customFieldName, value);

    default:
      return null;
  }

  return null;
};

/**
 * Build UI filters from filter groups
 */
export const buildUIFilters = (filterGroups) => {
  if (!filterGroups || filterGroups.length === 0) return [];

  // For each AND group, create an OR condition on userPhoneNumber or tag with appropriate operators
  return filterGroups
    .filter((g) => g?.ors && g.ors.length > 0)
    .map((g) => ({
      OR: g.ors
        .map((condition) => {
          const result = transformFilterCondition(condition);
          if (result) {
            console.log(`${condition.type} filter:`, condition, "→", result); // Debug log
          }
          return result;
        })
        .filter(Boolean),
    }))
    .filter((group) => group.OR.length > 0); // Filter out groups with empty OR arrays
};
